/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

/* eslint-disable react/no-unknown-property */
import classNames from "classnames";
import React, { ReactNode, useEffect, useRef, useState } from "react";

import { usePlayerConfig } from "../../shared/context/PlayerConfigContext";
import { usePlayerTranslation } from "../../shared/context/PlayerTranslationContext";
import { useDeviceType } from "../../shared/hooks/useDeviceType";
import { PlayerControllerType } from "../../shared/types";
import { PlayerVolumeProvider } from "../Controller";
import { useCustomPlayerContext } from "../CustomPlayerContext";

import { setCastReceiverAppId } from "./ChromecastSender";
import { getController } from "./loader";
import { PlayerAdvertisementProvider } from "./PlayerAdvertisementContext";
import { setPlayerRuntimeConfig } from "./playerConfig";
import { PlayerControllerProvider } from "./PlayerControllerContext";
import { usePlayerFeatures } from "./PlayerFeaturesContext";
import { PlayerTimerProvider } from "./PlayerTimerContext";
import { PlayerController } from "./types";

import "./BasePlayer.scss";

interface BasePlayerProps {
  children: ReactNode;
}

export const BasePlayer = (props: BasePlayerProps) => {
  const { children } = props;
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const imaContainerRef = useRef<HTMLDivElement>(null);
  const videoElementRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<PlayerController>();
  const [loaded, setLoaded] = useState<boolean>(false);

  const { withClientSideAds } = usePlayerFeatures();
  const { playerType } = usePlayerConfig();
  const { t } = usePlayerTranslation();
  const { chromecastReceiverId, withCredentials, liveTuning } =
    useCustomPlayerContext();

  // Set runtime config for non-React player classes
  useEffect(() => {
    setPlayerRuntimeConfig({
      withCredentials,
      liveTuning,
      chromecastReceiverId,
    });
    setCastReceiverAppId(chromecastReceiverId);
  }, [withCredentials, liveTuning, chromecastReceiverId]);

  const { iPadDevice, iPhoneDevice } = useDeviceType();

  const isAppleDevice = iPadDevice || iPhoneDevice;
  const isShakaPlayerAttached = playerType === PlayerControllerType.ShakaPlayer;

  useEffect(() => {
    if (
      videoElementRef.current &&
      imaContainerRef.current &&
      !playerRef.current
    ) {
      const session = localStorage.getItem("session");
      let token = "";

      if (session) {
        try {
          token = (JSON.parse(session) as { Token?: string }).Token || "";
        } catch {
          // ignore parse error
        }
      }

      const player = getController(
        {
          videoElement: videoElementRef.current,
          imaContainer: imaContainerRef.current,
          onReady: () => {
            setLoaded(true);
          },
          onReload: () => setLoaded(false),
          withClientSideAds,
          isAppleDevice,
          sessionToken: token,
        },
        playerType,
      );

      playerRef.current = player;
      player.initialize();
    }
  }, [withClientSideAds, isAppleDevice]);

  useEffect(() => {
    return () => {
      playerRef.current?.dispose();
    };
  }, []);

  return (
    <div
      className="BasePlayer"
      ref={playerContainerRef}
      role="region"
      aria-label={t("PLAYER_ARIA_VIDEO_PLAYER")}
    >
      <video
        ref={videoElementRef}
        className={classNames("BasePlayer__Video", {
          "BasePlayer__Video--Shaka": isShakaPlayerAttached,
        })}
        aria-label={t("PLAYER_ARIA_VIDEO")}
        playsInline
        x-webkit-airplay="allow"
      />
      <div ref={imaContainerRef} className="BasePlayer__ImaContainer" />

      {loaded && playerRef.current && (
        <PlayerControllerProvider
          player={playerRef.current}
          containerRef={playerContainerRef}
        >
          <PlayerTimerProvider player={playerRef.current}>
            <PlayerAdvertisementProvider>
              <PlayerVolumeProvider player={playerRef.current}>
                {children}
              </PlayerVolumeProvider>
            </PlayerAdvertisementProvider>
          </PlayerTimerProvider>
        </PlayerControllerProvider>
      )}
    </div>
  );
};
